// ─── Angular Imports ───────────────────────────────────────────────────────────
import { Component, OnInit, OnDestroy, isDevMode, ChangeDetectionStrategy } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';

// ─── Angular Material Imports ─────────────────────────────────────────────────
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';

// ─── Third-Party Imports ───────────────────────────────────────────────────────
import { Subject, takeUntil } from 'rxjs';

// ─── Application Imports ───────────────────────────────────────────────────────
import { BatchesService } from 'src/app/services/batches.services';
import { OrganizationsService } from 'src/app/services/organizations.service';
import { BatchNew } from 'src/app/models/batches/batchNew';
import { ValidationErrors } from 'src/app/models/validationErrors';
import { IOrganization } from 'src/app/models/organizations/organization';

@Component({
    selector: 'app-batch-create',
    templateUrl: './batch-create.component.html',
    styleUrls: ['./batch-create.component.scss'],
    changeDetection: ChangeDetectionStrategy.Default,
    standalone: false
})
export class BatchCreateComponent implements OnInit, OnDestroy {
  // ─── Observables ─────────────────────────────────────────────────────────────
  private destroy$ = new Subject<void>();

  // ─── State ───────────────────────────────────────────────────────────────────
  errorState: boolean = false;
  newBatch: BatchNew = new BatchNew();
  errorMessages: ValidationErrors[] = [];
  validatorProperties: string[] = [];
  organizations: IOrganization[] = [];
  organizationsSubscription: any;

  // ─── Form Controls ───────────────────────────────────────────────────────────
  nameFormControl: FormControl;
  jobNumberFormControl: FormControl;
  organizationFormControl: FormControl;

  // ─── Constructor ─────────────────────────────────────────────────────────────
  constructor(
    private batchesService: BatchesService,
    private organizationsService: OrganizationsService,
    public dialog: MatDialog,
    public dialogRef: MatDialogRef<BatchCreateComponent>,
    private snackBar: MatSnackBar,
  ) {
    this.nameFormControl = new FormControl('', [Validators.required]);
    this.jobNumberFormControl = new FormControl('', [Validators.required]);
    this.organizationFormControl = new FormControl('', [Validators.required]);
  }

  // ─── Lifecycle Hooks ────────────────────────────────────────────────────────
  ngOnInit(): void {
    this.loadOrganizations();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ─── Data Fetching Methods ──────────────────────────────────────────────────
  private loadOrganizations(): void {
    this.organizationsSubscription = this.organizationsService
      .listOrganizations()
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (organizations) => {
          if (organizations) {
            this.organizations = organizations;
            if (isDevMode()) {
              console.log('[DEV] [BatchCreateComponent] organizations found: ', organizations);
            }
          }
        },
        (error) => {
          console.error(error);
        },
      );
  }

  // ─── Action Methods ─────────────────────────────────────────────────────────
  createBatch(): void {
    this.batchesService
      .createBatch(this.newBatch)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        () => {
          this.snackBar.open('Successfully Created Batch', 'close', {
            horizontalPosition: 'center',
            verticalPosition: 'bottom',
            duration: 2000,
            panelClass: ['success'],
          });
          this.dialogRef.close();
        },
        (error) => this.handleCreateBatchError(error),
      );
  }

  private handleCreateBatchError(error: any): void {
    this.errorState = true;
    if (error.status === 422) {
      this.errorMessages = [];
      this.validatorProperties = [];
      error.error.validationErrors.forEach((validationError: ValidationErrors) => {
        this.errorMessages.push(validationError);
        this.validatorProperties.push(validationError.property);

        this.applyValidationError(validationError);
      });
    }
  }

  private applyValidationError(validationError: ValidationErrors): void {
    const { property } = validationError;

    if (property === 'Name') {
      this.nameFormControl.setErrors({ serverErrors: 'validation errors' });
      this.nameFormControl.markAllAsTouched();
    }

    if (property === 'JobNumber') {
      this.jobNumberFormControl.setErrors({
        serverErrors: 'validation errors',
      });
      this.jobNumberFormControl.markAllAsTouched();
    }

    if (property === 'OrganizationId') {
      this.organizationFormControl.setErrors({
        serverErrors: 'validation errors',
      });
      this.organizationFormControl.markAllAsTouched();
    }
  }

  validationMessage(property: string): string | undefined {
    return this.errorMessages.find((error) => error.property === property)?.message;
  }
}
